import { useState, useRef, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Mail, KeyRound, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { portalAuthAPI } from '../../lib/portalApi';
import { usePortalAuth } from '../../context/PortalAuthContext';
import { BRAND_ASSETS } from '../../config/brandAssets';

const LOGO_URL = BRAND_ASSETS.LOGO_WORDMARK;

const SAFE_FONT = "'Century Gothic','CenturyGothic','Manrope','DM Sans',Arial,sans-serif";

const PortalLogin = () => {
    const { t } = useTranslation('portal');
    const navigate = useNavigate();
    const { profile, setSession } = usePortalAuth();
    const [step, setStep] = useState('email');           // 'email' | 'otp'
    const [email, setEmail] = useState('');
    const [emailMasked, setEmailMasked] = useState('');
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const codeInputRef = useRef(null);

    // Cooldown timer for "Resend code" button (60s)
    useEffect(() => {
        if (resendCooldown <= 0) return undefined;
        const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [resendCooldown]);

    // Already authenticated? Skip the form.
    if (profile) {
        return <Navigate to="/portal" replace />;
    }

    const requestCode = async (e) => {
        if (e) e.preventDefault();
        const trimmed = email.trim().toLowerCase();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            toast.error(t('login.errors.invalid_email'));
            return;
        }
        setLoading(true);
        try {
            const { data } = await portalAuthAPI.requestOtp(trimmed, 'en');
            setEmailMasked(data.email_masked || trimmed);
            setStep('otp');
            setResendCooldown(60);
            setTimeout(() => codeInputRef.current?.focus(), 100);
            toast.success(t('login.toasts.code_sent'));
        } catch (err) {
            const msg = err.response?.data?.detail || t('login.errors.send_failed');
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const verifyCode = async (e) => {
        if (e) e.preventDefault();
        const clean = code.replace(/\D/g, '');
        if (clean.length !== 6) {
            toast.error(t('login.errors.code_length'));
            return;
        }
        setLoading(true);
        try {
            const { data } = await portalAuthAPI.verifyOtp(email.trim().toLowerCase(), clean);
            await setSession({ token: data.token, email: data.email });
            toast.success(t('login.toasts.signed_in'));
            navigate('/portal', { replace: true });
        } catch (err) {
            const msg = err.response?.data?.detail || t('login.errors.verify_failed');
            toast.error(msg);
            setCode('');
            codeInputRef.current?.focus();
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            data-testid="portal-login-page"
            className="min-h-screen flex items-stretch"
            style={{ fontFamily: SAFE_FONT, background: '#FBF8F6' }}
        >
            {/* Hero — black panel with the logo. Hidden on mobile. */}
            <div className="hidden lg:flex flex-col bg-[#000000] relative w-1/2">
                <div className="flex-1" />
                <div className="p-12 pb-16">
                    <img src={LOGO_URL} alt="UNIVERS COSMETIC" className="w-72 h-auto object-contain mb-6" />
                    <p className="text-xs uppercase tracking-[4px] text-[#C08478] mb-3">{t('login.hero_subtitle')}</p>
                    <p className="text-lg text-white/70">{t('login.hero_description')}</p>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C08478] to-transparent" />
            </div>

            {/* Form panel */}
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-sm">
                    <div className="mb-10">
                        <p className="text-xs uppercase tracking-[3px] text-[#C08478] font-semibold mb-2">{t('login.hero_subtitle')}</p>
                        <h1 className="text-3xl font-bold text-[#1A1A1A] mb-2">
                            {step === 'email' ? t('login.title_email') : t('login.title_code')}
                        </h1>
                        <p className="text-sm text-gray-500">
                            {step === 'email'
                                ? t('login.subtitle_email')
                                : (
                                    <>
                                        {t('login.subtitle_code_prefix')} <span className="font-medium text-[#1A1A1A]">{emailMasked}</span>.
                                        {' '}{t('login.subtitle_code_suffix')}
                                    </>
                                )}
                        </p>
                    </div>

                    {step === 'email' && (
                        <form onSubmit={requestCode} className="space-y-5" data-testid="portal-login-email-form">
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-sm font-medium text-gray-700">{t('login.field_email')}</Label>
                                <div className="relative">
                                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <Input
                                        id="email"
                                        data-testid="portal-login-email-input"
                                        type="email"
                                        autoFocus
                                        autoComplete="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="you@company.com"
                                        className="h-12 pl-10 bg-[#F5F5F5] border-[#E5E5E5] focus:border-[#C08478] focus:ring-[#C08478]/20"
                                    />
                                </div>
                            </div>

                            <Button
                                type="submit"
                                disabled={loading}
                                data-testid="portal-login-send-code-btn"
                                className="w-full h-12 bg-[#1A1A1A] hover:bg-[#333] text-white rounded-full font-medium transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                            >
                                {loading ? (
                                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {t('login.cta_sending')}</>
                                ) : t('login.cta_send')}
                            </Button>
                        </form>
                    )}

                    {step === 'otp' && (
                        <form onSubmit={verifyCode} className="space-y-5" data-testid="portal-login-otp-form">
                            <div className="space-y-2">
                                <Label htmlFor="code" className="text-sm font-medium text-gray-700">{t('login.field_code')}</Label>
                                <div className="relative">
                                    <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <Input
                                        id="code"
                                        ref={codeInputRef}
                                        data-testid="portal-login-otp-input"
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        autoComplete="one-time-code"
                                        maxLength={6}
                                        value={code}
                                        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                        placeholder="••••••"
                                        className="h-12 pl-10 bg-[#F5F5F5] border-[#E5E5E5] focus:border-[#C08478] focus:ring-[#C08478]/20 text-center tracking-[10px] font-mono text-lg"
                                    />
                                </div>
                            </div>

                            <Button
                                type="submit"
                                disabled={loading || code.length !== 6}
                                data-testid="portal-login-verify-btn"
                                className="w-full h-12 bg-[#1A1A1A] hover:bg-[#333] text-white rounded-full font-medium transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5"
                            >
                                {loading ? (
                                    <><Loader2 className="w-4 h-4 animate-spin mr-2" /> {t('login.cta_verifying')}</>
                                ) : t('login.cta_verify')}
                            </Button>

                            <div className="flex items-center justify-between text-sm pt-2">
                                <button
                                    type="button"
                                    onClick={() => { setStep('email'); setCode(''); }}
                                    className="text-gray-500 hover:text-[#1A1A1A] flex items-center gap-1"
                                    data-testid="portal-login-change-email-btn"
                                >
                                    <ArrowLeft size={14} /> {t('login.change_email')}
                                </button>
                                <button
                                    type="button"
                                    disabled={resendCooldown > 0 || loading}
                                    onClick={() => requestCode()}
                                    data-testid="portal-login-resend-btn"
                                    className="text-[#C08478] hover:text-[#A0695E] disabled:text-gray-400 disabled:cursor-not-allowed font-medium"
                                >
                                    {resendCooldown > 0 ? t('login.resend_in', { seconds: resendCooldown }) : t('login.resend')}
                                </button>
                            </div>
                        </form>
                    )}

                    <p className="mt-10 text-xs text-gray-400 text-center">
                        {t('login.admin_link_prefix')} <a href="/login" className="text-[#C08478] hover:underline">{t('login.admin_link')}</a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default PortalLogin;
